import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, getPublicWedding, getPublicWeddingByDomain } from '../lib/api';

const ContentContext = createContext(null);

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ slug, subdomain, children }) => {
  const [content, setContent] = useState(null);
  const [resolvedSlug, setResolvedSlug] = useState(slug || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = useCallback(async () => {
    // Subdomain mode: fetch by custom_domain
    if (subdomain) {
      try {
        setLoading(true);
        const data = await getPublicWeddingByDomain(subdomain);
        const { _slug, _customDomain, ...contentData } = data;
        setResolvedSlug(_slug);
        setContent(contentData);
        setError(null);
      } catch (err) {
        console.warn('Failed to load wedding by subdomain:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Slug mode: fetch from /api/w/:slug (for public wedding pages)
    if (slug !== undefined) {
      try {
        setLoading(true);
        const data = await getPublicWedding(slug);
        setContent(data);
        setResolvedSlug(slug);
        setError(null);
      } catch (err) {
        console.warn('Failed to load wedding content:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Legacy mode: fetch from /api/content (for admin dashboard)
    try {
      setLoading(true);
      const data = await api.getContent();
      setContent(data);
      setError(null);
    } catch (err) {
      console.warn('Failed to load CMS content, components will use fallback defaults:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [slug, subdomain]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const refreshContent = useCallback(async () => {
    try {
      if (subdomain) {
        const data = await getPublicWeddingByDomain(subdomain);
        const { _slug, _customDomain, ...contentData } = data;
        setResolvedSlug(_slug);
        setContent(contentData);
      } else if (slug) {
        const data = await getPublicWedding(slug);
        setContent(data);
      } else {
        const data = await api.getContent();
        setContent(data);
      }
      setError(null);
    } catch (err) {
      console.warn('Failed to refresh content:', err.message);
    }
  }, [slug, subdomain]);

  return (
    <ContentContext.Provider value={{ content, loading, error, refreshContent, slug: resolvedSlug, subdomain }}>
      {children}
    </ContentContext.Provider>
  );
};
